import { useNavigate } from '@tanstack/react-router'
import { useCustomerAppointments } from '@/api/hooks/customer.hooks'
import { Calendar, User, Clock } from 'lucide-react'
import { formatAppointmentTime, formatDisplayDate } from '@/utils/date'
import { StatusBadge } from '@/components/ui/StatusBadge'

export default function PatientDashboard() {
  const navigate = useNavigate()
  const { data, isLoading } = useCustomerAppointments({
    take: 5,
    skip: 0,
  })

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div className='h-8 bg-muted animate-pulse rounded' />
        <div className='bg-white rounded-xl border border-border p-6 space-y-4'>
          {[1, 2, 3].map((i) => (
            <div key={i} className='h-20 bg-muted animate-pulse rounded' />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-2xl font-bold text-foreground'>Client Area</h2>
      </div>
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        <div className='lg:col-span-2 bg-white rounded-xl border border-border p-6'>
          <div className='flex items-center justify-between mb-4'>
            <h3 className='text-xl font-semibold text-foreground'>Recent Appointments</h3>
            <button
              onClick={() => navigate({ to: '/client-area/appointments' })}
              className='text-sm font-medium text-primary hover:text-primary/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md px-2 py-1'
            >
              View all
            </button>
          </div>
          {data?.length === 0 ? (
            <div className='text-center py-12'>
              <Calendar className='w-16 h-16 text-muted-foreground mx-auto mb-4' />
              <p className='text-muted-foreground mb-4'>No appointments scheduled</p>
              <button
                onClick={() => navigate({ to: '/' })}
                className='px-4 py-2 bg-gradient-primary text-white rounded-lg hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
              >
                Book Appointment
              </button>
            </div>
          ) : (
            <div className='space-y-3'>
              {data?.slice(0, 3).map((appointment) => (
                <div
                  key={appointment.id}
                  className='p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer'
                  onClick={() => navigate({ to: '/client-area/appointments/$id', params: { id: appointment.id } })}
                >
                  <div className='flex items-start justify-between gap-4'>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-start justify-between gap-3 mb-2'>
                        <div className='flex-1 min-w-0'>
                          <h3 className='font-semibold text-foreground truncate'>
                            {appointment.specialistName}
                          </h3>
                          {appointment.practiceName && (
                            <p className='text-sm text-muted-foreground mt-0.5 truncate'>
                              {appointment.practiceName}
                            </p>
                          )}
                        </div>
                        <div className='flex flex-col items-end gap-2'>
                          <StatusBadge status={appointment.status} type='primary' />
                        </div>
                      </div>
                      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                        <Calendar className='w-4 h-4 flex-shrink-0' />
                        <span>{formatDisplayDate(appointment.startTime)}</span>
                        <span className='text-muted-foreground/60'>•</span>
                        <Clock className='w-4 h-4 flex-shrink-0' />
                        <span>{formatAppointmentTime(appointment.startTime)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className='bg-white rounded-xl border border-border p-6'>
          <h3 className='text-xl font-semibold text-foreground mb-4'>Quick Actions</h3>
          <div className='space-y-3'>
            <button
              onClick={() => navigate({ to: '/' })}
              className='w-full text-left px-4 py-3 rounded-lg border border-border hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            >
              <div className='flex items-center gap-3'>
                <Calendar className='w-5 h-5 text-primary' />
                <span className='font-medium'>Book Appointment</span>
              </div>
            </button>
            <button
              onClick={() => navigate({ to: '/client-area/appointments' })}
              className='w-full text-left px-4 py-3 rounded-lg border border-border hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            >
              <div className='flex items-center gap-3'>
                <Calendar className='w-5 h-5 text-primary' />
                <span className='font-medium'>View Appointments</span>
              </div>
            </button>
            <button
              onClick={() => navigate({ to: '/client-area/profile' })}
              className='w-full text-left px-4 py-3 rounded-lg border border-border hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            >
              <div className='flex items-center gap-3'>
                <User className='w-5 h-5 text-primary' />
                <span className='font-medium'>My Profile</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
